<template>
  <v-container fluid>
    <v-data-table
      :headers="headers"
      :items="items"
      class="elevation-1"
      :search="search"
      :footer-props="{ itemsPerPageOptions: [50, 100, 200, 500, -1] }"
      dense
    >
      <template v-slot:top>
        <v-toolbar flat>
          <v-toolbar-title class="primary--text"
            >Keywords Picker Setup</v-toolbar-title
          >
          <v-divider class="mx-4" inset vertical></v-divider>
          <v-spacer></v-spacer>
          <v-text-field
            v-model="search"
            append-icon="mdi-magnify"
            label="Search"
            single-line
            hide-details
            class="mr-3"
          ></v-text-field>
          <v-btn
            class="mx-2"
            fab
            dark
            x-small
            color="secondary"
            @click="loadItems"
            title="Update"
          >
            <v-icon dark color="primary"> mdi-refresh </v-icon>
          </v-btn>
          <v-dialog v-model="dialog" max-width="70%">
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                class="mx-2"
                fab
                dark
                x-small
                color="secondary"
                v-bind="attrs"
                v-on="on"
                title="Add new item"
              >
                <v-icon dark color="primary"> mdi-plus </v-icon>
              </v-btn>
            </template>
            <v-card>
              <v-card-title>
                <span class="headline primary--text">{{ formTitle }}</span>
              </v-card-title>

              <v-card-text>
                <v-form ref="form" v-model="valid">
                  <v-row>
                    <v-col cols="12" sm="4" md="4">
                      <v-text-field
                        v-model="editedItem.name"
                        ref="name"
                        label="Name"
                        :error-messages="errors.name"
                        :error-count="errors.name.length"
                        @change="errors.name = []"
                      ></v-text-field>
                    </v-col>

                    <v-col cols="12" sm="4" md="4">
                      <v-autocomplete
                        v-model="editedItem.partner"
                        ref="partner"
                        :items="partners"
                        item-value="id"
                        item-text="name"
                        label="Partner"
                        @change="updateEditedModelRoi"
                        :hint="editedPartnerIdLabel"
                        persistent-hint
                        :error-messages="errors['partner.name']"
                        :error-count="errors['partner.name'].length"
                        return-object
                      ></v-autocomplete>
                    </v-col>

                    <v-col cols="12" sm="4" md="4">
                      <v-row>
                        <v-col cols="12" sm="4" md="4">
                          <v-checkbox
                            :disabled="!roi_desktop || roi_desktop <= 0"
                            v-model="editedItem.roi_desktop"
                            ref="roi_desktop"
                            color="success"
                            :value="roi_desktop"
                            @mouseup="validateRoiCheckboxes"
                          >
                            <template v-slot:label>
                              <v-tooltip bottom>
                                <template v-slot:activator="{ on }">
                                  <div v-on="on">
                                    <v-icon :color="roiColor('desktop')"
                                      >mdi-desktop-mac-dashboard</v-icon
                                    >
                                    <span
                                      v-if="roi_desktop > 0"
                                      :class="roiColor('desktop') + '--text'"
                                      >({{ roi_desktop }})</span
                                    >
                                  </div>
                                </template>
                                ROI id for <strong>desktop</strong>:
                                {{ roi_desktop }}
                              </v-tooltip>
                            </template>
                          </v-checkbox>
                        </v-col>
                        <v-col cols="12" sm="4" md="4">
                          <v-checkbox
                            :disabled="!roi_mobile || roi_mobile <= 0"
                            v-model="editedItem.roi_mobile"
                            ref="roi_mobile"
                            color="success"
                            :value="roi_mobile"
                            @mouseup="validateRoiCheckboxes"
                          >
                            <template v-slot:label>
                              <v-tooltip bottom>
                                <template v-slot:activator="{ on }">
                                  <div v-on="on">
                                    <v-icon :color="roiColor('mobile')"
                                      >mdi-cellphone-iphone</v-icon
                                    >
                                    <span
                                      v-if="roi_mobile > 0"
                                      :class="roiColor('mobile') + '--text'"
                                      >({{ roi_mobile }})</span
                                    >
                                  </div>
                                </template>
                                ROI id for <strong>mobile</strong>:
                                {{ editedItem.roi_mobile }}
                              </v-tooltip>
                            </template>
                          </v-checkbox>
                        </v-col>
                        <v-col cols="12" sm="4" md="4">
                          <v-checkbox
                            :disabled="!roi_tablet || roi_tablet <= 0"
                            v-model="editedItem.roi_tablet"
                            ref="roi_tablet"
                            color="success"
                            :value="roi_tablet"
                            @mouseup="validateRoiCheckboxes"
                          >
                            <template v-slot:label>
                              <v-tooltip bottom>
                                <template v-slot:activator="{ on }">
                                  <div v-on="on">
                                    <v-icon :color="roiColor('tablet')"
                                      >mdi-tablet-ipad</v-icon
                                    >
                                    <span
                                      v-if="roi_tablet > 0"
                                      :class="roiColor('tablet') + '--text'"
                                      >({{ roi_tablet }})</span
                                    >
                                  </div>
                                </template>
                                ROI id for <strong>tablet</strong>:
                                {{ editedItem.roi_tablet }}
                              </v-tooltip>
                            </template>
                          </v-checkbox>
                        </v-col>
                        <v-col
                          cols="12"
                          sm="12"
                          md="12"
                          class="pa-0 ma-0"
                          v-if="editedItem.partner && roiCheckboxHint"
                        >
                          <v-alert dense outlined type="error">
                            {{ roiCheckboxHint }}
                          </v-alert>
                        </v-col>
                      </v-row>
                    </v-col>

                    <v-col cols="12" sm="4" md="4">
                      <v-autocomplete
                        multiple
                        v-model="editedItem.source"
                        ref="source"
                        :items="available_sources"
                        label="Source"
                        :error-messages="errors.source"
                        :error-count="errors.source.length"
                        @change="errors.source = []"
                      ></v-autocomplete>
                    </v-col>

                    <v-col cols="12" sm="4" md="4">
                      <v-autocomplete
                        v-model="editedItem.revenue_partner"
                        ref="revenue_partner"
                        :items="revenue_partners"
                        label="Revenue partner"
                        :error-messages="errors.revenue_partner"
                        :error-count="errors.revenue_partner.length"
                        @change="errors.revenue_partner = []"
                      ></v-autocomplete>
                    </v-col>

                    <v-col cols="12" sm="4" md="4">
                      <v-autocomplete
                        v-model="editedItem.mode"
                        ref="mode"
                        :items="modes"
                        label="Mode"
                        :error-messages="errors.mode"
                        :error-count="errors.mode.length"
                        @change="errors.mode = []"
                      ></v-autocomplete>
                    </v-col>

                    <v-col cols="12" sm="4" md="4">
                      <v-select
                        v-model="
                          editedItem.parameters.related_keywords_provider
                        "
                        item-text="formatted_name"
                        item-value="alias"
                        :items="available_related_keywords_providers"
                        label="Related Keyword Provider"
                      ></v-select>
                    </v-col>

                    <v-col cols="12" sm="4" md="4">
                      <v-select
                        v-model="editedItem.parameters.share_processor_provider"
                        item-text="formatted_name"
                        item-value="alias"
                        :items="available_share_processor_providers"
                        label="Race-Handler Provider (Share processor)"
                      ></v-select>
                    </v-col>

                    <v-col cols="12" sm="4" md="4">
                      <v-slider
                        ref="agg_revenue_data_hours"
                        v-model="editedItem.parameters.agg_revenue_data_hours"
                        :label="
                          'Aggregated revenue data hours: ' +
                          editedItem.parameters.agg_revenue_data_hours
                        "
                        persistent-hint
                        :hint="aggRevenueDataHoursHint"
                        min="24"
                        max="72"
                        step="24"
                        thumb-label
                        ticks
                        thumb-color="secondary"
                        class="pt-4"
                        :error-messages="
                          errors['parameters.agg_revenue_data_hours']
                        "
                        :error-count="
                          errors['parameters.agg_revenue_data_hours'].length
                        "
                        @change="
                          errors['parameters.agg_revenue_data_hours'] = []
                        "
                      ></v-slider>
                    </v-col>

                    <v-col cols="12" sm="4" md="4">
                      <v-menu
                        ref="rangeMenu"
                        v-model="active_range_menu"
                        :close-on-content-click="false"
                        :return-value.sync="editedItem.active_range"
                        transition="scale-transition"
                        offset-y
                        min-width="290px"
                      >
                        <template v-slot:activator="{ on, attrs }">
                          <v-text-field
                            v-model="editedItem.active_range"
                            ref="active_range"
                            label="Active range"
                            prepend-icon="mdi-calendar"
                            readonly
                            v-bind="attrs"
                            v-on="on"
                            :error-messages="errors.active_range"
                            :error-count="errors.active_range.length"
                          ></v-text-field>
                        </template>
                        <v-date-picker
                          v-model="editedItem.active_range"
                          :min="editedItem.active_from ? editedItem.active_from : today"
                          range
                        >
                          <v-spacer></v-spacer>
                          <v-btn
                            color="grey lighten-2"
                            class="primary--text"
                            @click="active_range_menu = false"
                          >
                            Cancel
                          </v-btn>
                          <v-btn
                            color="primary"
                            class="secondary--text"
                            @click="saveRangeMenu(editedItem.active_range)"
                          >
                            Ok
                          </v-btn>
                        </v-date-picker>
                      </v-menu>
                    </v-col>

                    <v-col cols="12" sm="4" md="4" id="related_number">
                      <v-slider
                        v-model="editedItem.related_number"
                        ref="related_number"
                        min="2"
                        max="20"
                        thumb-color="secondary"
                        :label="'Related number: ' + editedItem.related_number"
                        persistent-hint
                        :hint="relatedNumberHint"
                        class="pt-4"
                        :error-messages="errors.related_number"
                        :error-count="errors.related_number.length"
                        @change="errors.related_number = []"
                      >
                      </v-slider>
                    </v-col>

                    <v-col
                      cols="12"
                      sm="4"
                      md="4"
                      v-if="editedItem.mode == 'scouting'"
                    >
                      <v-slider
                        v-if="editedItem.mode == 'scouting'"
                        v-model="editedItem.primary_percentage"
                        min="20"
                        max="80"
                        thumb-color="secondary"
                        :label="
                          'Primary Keyword percentage: ' +
                          editedItem.primary_percentage +
                          ' %'
                        "
                        persistent-hint
                        class="pt-4"
                        hint="For Scouting mode only"
                        :error-messages="errors.primary_percentage"
                        :error-count="errors.primary_percentage.length"
                        @change="errors.primary_percentage = []"
                      >
                      </v-slider>
                    </v-col>

                    <v-col cols="12" sm="4" md="4">
                      <v-text-field
                        ref="impressions_threshold"
                        type="number"
                        placeholder="Default: 5 impressions"
                        label="Impressions threshold"
                        :min="0"
                        :max="30"
                        v-model.number="
                          editedItem.parameters.impressions_threshold
                        "
                        :error-messages="
                          errors['parameters.impressions_threshold']
                        "
                        :error-count="
                          errors['parameters.impressions_threshold'].length
                        "
                        @change="
                          errors['parameters.impressions_threshold'] = []
                        "
                      ></v-text-field>
                    </v-col>
                  </v-row>
                </v-form>
              </v-card-text>

              <v-card-actions>
                <v-alert
                  v-if="showErrorMessage"
                  outlined
                  border="left"
                  type="error"
                  icon="mdi-cloud-alert"
                >
                  {{ formErrorMessage }}
                </v-alert>
                <v-spacer></v-spacer>
                <v-btn
                  color="grey lighten-2"
                  class="primary--text"
                  @click="close"
                  >Cancel</v-btn
                >
                <v-btn
                  color="primary"
                  class="secondary--text"
                  @click="save"
                  :disabled="!valid"
                  >Save</v-btn
                >
              </v-card-actions>
            </v-card>
          </v-dialog>
          <v-dialog v-model="dialogDelete" max-width="500px">
            <v-card>
              <v-card-title class="headline"
                >Are you sure you want to delete this item?</v-card-title
              >
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn
                  color="primary"
                  class="secondary--text"
                  @click="closeDelete"
                  >Cancel</v-btn
                >
                <v-btn
                  color="error"
                  class="white--text"
                  @click="deleteItemConfirm"
                  >OK</v-btn
                >
                <v-spacer></v-spacer>
              </v-card-actions>
            </v-card>
          </v-dialog>
        </v-toolbar>
      </template>
      <template v-slot:item.partner.name="{ item }">
        {{ item.partner.name
        }}<span class="smallcaption"> #{{ item.partner.id }}</span>
      </template>
      <template v-slot:item.roi_desktop="{ item }">
        <v-icon small :color="item.roi_desktop > 0 ? 'success' : 'error'"
          >mdi-desktop-mac-dashboard</v-icon
        >
        <span class="smallcaption">#{{ item.roi_desktop }}</span>
      </template>
      <template v-slot:item.roi_mobile="{ item }">
        <v-icon small :color="item.roi_mobile > 0 ? 'success' : 'error'"
          >mdi-cellphone-iphone</v-icon
        ><span class="smallcaption">#{{ item.roi_mobile }}</span>
      </template>
      <template v-slot:item.roi_tablet="{ item }">
        <v-icon small :color="item.roi_tablet > 0 ? 'success' : 'error'"
          >mdi-tablet-ipad</v-icon
        >
        <span class="smallcaption">#{{ item.roi_tablet }}</span>
      </template>
      <template v-slot:item.primary_percentage="{ item }">
        <div>
          <span v-if="item.primary_percentage < 1">N/A</span>
          <v-progress-linear
            v-else
            color="secondary"
            v-model="item.primary_percentage"
            height="18"
          >
            <strong class="">{{ item.primary_percentage }}%</strong>
          </v-progress-linear>
        </div>
      </template>

      <template v-slot:item.mode="{ item }">
        {{ formatMode(item.mode) }}
      </template>

      <template v-slot:item.related_keywords_provider="{ item }">
        {{
          formatRelatedKeywordProvider(
            item.parameters.related_keywords_provider
          )
        }}
      </template>

      <template v-slot:item.agg_revenue_data_hours="{ item }">
        {{ item.parameters.agg_revenue_data_hours }}
      </template>

      <template v-slot:item.impressions_threshold="{ item }">
        {{ item.parameters.impressions_threshold }}
      </template>

      <template v-slot:item.active_from="{ item }">
        {{ item.active_from | formatDate }}
      </template>
      <template v-slot:item.active_to="{ item }">
        {{ item.active_to | formatDate }}
      </template>
      <template v-slot:item.actions="{ item }">
        <v-dialog v-model="dialogDetails" scrollable max-width="90%">
          <template v-slot:activator="{ on, attrs }">
            <v-icon
              v-bind="attrs"
              v-on="on"
              small
              class="mr-2"
              color="secondary"
              title="Audits"
              @click="setEditedItem(item)"
            >
              mdi-history
            </v-icon>
          </template>
          <v-card>
            <v-card-title class="primary--text" v-if="editedItem.partner">
              Setup's audits for:&nbsp;
              <em>
                <strong>{{ editedItem.name }}</strong>
                ({{ editedItem.partner.name }}) {{ editedItem.source }} on
                {{ editedItem.revenue_partner }}
              </em>
            </v-card-title>
            <v-divider></v-divider>
            <v-card-text style="height: 500px" class="pt-5">
              <v-data-table
                :headers="auditsHeaders"
                :items="details.audits"
                :loading="detailsLoading"
                loading-text="Loading... Please wait"
                hide-default-footer
                :items-per-page="10000"
              >
                <template v-slot:item.event="{ item }">
                  <v-avatar :color="eventColor(item.event)" :title="item.event">
                    <v-icon dark>
                      {{ eventIcon(item.event) }}
                    </v-icon>
                  </v-avatar>
                </template>
                <template v-slot:item.user="{ item }">
                  <a
                    :href="'mailto:' + item.user_email"
                    :title="item.user_name"
                  >
                    {{ item.user_name }}
                  </a>
                </template>
                <template v-slot:item.old_values="{ item }">
                  <vue-json-pretty :data="item.old_values"> </vue-json-pretty>
                </template>
                <template v-slot: :item.new_values="{ item }">
                  <vue-json-pretty :data="item.new_values"> </vue-json-pretty>
                </template>
              </v-data-table>
            </v-card-text>
          </v-card>
        </v-dialog>
        <v-icon
          small
          class="mr-2"
          color="primary"
          @click="editItem(item)"
          title="Edit item"
        >
          mdi-pencil
        </v-icon>
        <v-icon
          small
          color="error"
          @click="deleteItem(item)"
          title="Delete item"
        >
          mdi-delete
        </v-icon>
      </template>
    </v-data-table>
  </v-container>
</template>

<script>
import { api } from "#/Api.js";
import { mapActions, mapGetters } from "vuex";
import axios from "#/plugins/Axios.js";
import moment from "moment";
import VueJsonPretty from "vue-json-pretty";
import "vue-json-pretty/lib/styles.css";

export default {
  name: "Admin_Modules_Setup",

  components: {
    VueJsonPretty,
  },

  data: () => ({
    active_from_menu: false,
    active_range_menu: false,
    active_to_menu: false,
    auditsHeaders: [
      { text: "Event", value: "event" },
      { text: "Event time", align: "end", value: "created_at" },
      { text: "User", value: "user" },
      //{ text: 'User', value: 'user_name' },
      //{ text: 'Email', value: 'user_email' },
      { text: "IP Address", align: "end", value: "ip_address" },
      { text: "User Agent", value: "user_agent" },
      { text: "Old value", value: "old_values", sortable: false },
      { text: "New value", value: "new_values", sortable: false },
    ],
    defaultItem: {
      partner: null,
      roi_desktop: null,
      roi_mobile: null,
      roi_tablet: null,
      source: [],
      name: "",
      revenue_partner: "",
      mode: "keywords_spread",
      related_number: 4,
      primary_percentage: 0,
      active_range: null,
      parameters: {
        related_keywords_provider: "yahoo_related_keywords_provider",
        share_processor_provider: "two_lanes_share_processor",
        agg_revenue_data_hours: 48,
        impressions_threshold: 5,
      },
    },
    defaultDetails: {
      audits: [],
    },
    details: {
      audits: [],
    },
    detailsLoading: false,
    deviceTypes: ["desktop", "mobile", "tablet"],
    dialog: false,
    dialogDelete: false,
    dialogDetails: false,
    editedIndex: -1,
    editedItem: null /* {
      partner: null,
      roi_desktop: null,
      roi_mobile: null,
      roi_tablet: null,
      source: null,
      name: "",
      revenue_partner: "",
      mode: "keywords_spread",
      related_number: 4,
      primary_percentage: 0,
      active_range: null,
      parameters: {
        related_keywords_provider: "yahoo_related_keywords_provider",
        share_processor_provider: "two_lanes_share_processor",
      },
    }, */,
    errors: {
      "partner.name": [],
      roi_desktop: [],
      roi_mobile: [],
      roi_tablet: [],
      source: [],
      name: [],
      revenue_partner: [],
      mode: [],
      related_number: [],
      "parameters.agg_revenue_data_hours": [],
      "parameters.impressions_threshold": [],
      primary_percentage: [],
      active_range: [],
    },
    formErrorMessage: "",
    headers: [
      { text: "#", value: "id", align: "end", width: "60px" },
      { text: "Name", align: "start", value: "name" },
      { text: "Partner", value: "partner.name" },
      { text: "Desktop", value: "roi_desktop", align: "left", width: "100px" },
      { text: "Mobile", value: "roi_mobile", align: "left", width: "100px" },
      { text: "Tablet", value: "roi_tablet", align: "left", width: "100px" },
      { text: "Source", value: "source" },
      { text: "Revenue partner", value: "revenue_partner" },
      { text: "Mode", value: "mode" },
      {
        text: "Rel. Keywords prov.",
        value: "related_keywords_provider",
        width: "180px",
      },
      {
        text: "Agg. Rev. Data Hours",
        value: "agg_revenue_data_hours",
        width: "100px",
      },
      { text: "Impr. thres.", value: "impressions_threshold", width: "80px" },
      { text: "From", value: "active_from", align: "end" },
      { text: "To", value: "active_to", align: "end" },
      { text: "Related", value: "related_number", align: "end" },
      // { text: "Primary %", value: "primary_percentage", align: "center" },
      {
        text: "Actions",
        value: "actions",
        sortable: false,
        align: "end",
        width: "105px",
      },
    ],
    items: [],
    roi_desktop: null,
    roi_mobile: null,
    roi_tablet: null,
    search: "",
    today: moment().format("YYYY-MM-DD"),
    valid: false,
  }),

  methods: {
    ...mapActions("app", ["updateBreadcrumbs", "startLoading", "stopLoading"]),
    ...mapActions("notifier", [
      "setInfo",
      "setMessage",
      "setSuccess",
      "setError",
      "setWarning",
    ]),

    initialize() {
      this.updateBreadcrumbs([
        {
          text: "Setup",
          disabled: true,
          to: { name: "app.setup" },
        },
      ]);

      this.editedItem = _.defaultsDeep(this.editedItem, this.defaultItem);
      this.loadItems();
    },

    loadItems() {
      this.startLoading();
      this.items = [];

      api
        .get("setup")
        .then((response) => {
          this.items = response.data.data;
          let _this = this;
          this.items.forEach(function (item, index) {
            item.active_range.forEach(function (date, dateIndex) {
              if (_this.items[index].active_range[dateIndex]) {
                _this.items[index].active_range[dateIndex] = moment(
                  String(_this.items[index].active_range[dateIndex])
                ).format("YYYY-MM-DD");
              }
            });
          });
        })
        .catch((error) => {
          this.setError(error);
        })
        .finally(() => {
          this.stopLoading();
        });
    },

    setEditedItem(item) {
      this.editedIndex = this.items.indexOf(item);
      //this.editedItem = Object.assign({}, item);
      this.editedItem = _.defaultsDeep({ ...item }, this.defaultItem);
    },

    editItem(item) {
      this.setEditedItem(item);
      let _this = this;
      this.deviceTypes.forEach(function (device) {
        let attribute = "roi_" + device.toLowerCase();
        if (_this.editedItem[attribute] && _this.editedItem[attribute] > 0) {
          _this[attribute] = _this.editedItem[attribute];
        }
      });

      this.updateEditedModelRoi(item.partner);
      this.dialog = true;
    },

    deleteItem(item) {
      this.editedIndex = this.items.indexOf(item);
      this.editedItem = Object.assign({}, item);
      this.dialogDelete = true;
    },

    deleteItemConfirm() {
      this.startLoading();
      let url = api.path("setup") + "/" + this.editedItem.id;
      axios
        .delete(url, this.editedItem)
        .then((response) => {
          this.items.splice(this.editedIndex, 1);
          this.setSuccess("Item removed correctly");
          this.closeDelete();
        })
        .catch((error) => {
          console.log(error);
          this.setError(error);
        })
        .finally(() => {
          this.stopLoading();
        });
    },

    close() {
      this.dialog = false;
      this.$nextTick(() => {
        this.editedItem = Object.assign({}, this.defaultItem);
        this.editedIndex = -1;
      });
      this.resetForm();
    },

    closeDelete() {
      this.dialogDelete = false;
      this.$nextTick(() => {
        this.editedItem = Object.assign({}, this.defaultItem);
        this.editedIndex = -1;
      });
    },

    closeDetails() {
      this.dialogDetails = false;
      this.$nextTick(() => {
        this.details = Object.assign({}, this.defaultDetails);
      });
    },

    async save() {
      this.startLoading();
      let method = "post";
      let url = api.path("setup");
      let successMessage = "Item saved correctly";

      if (this.editedItem.id) {
        // if the item has an id, we're updating an existing item
        method = "put";
        url = url + "/" + this.editedItem.id;
        successMessage = "Item updated correctly";
      }

      // save the record
      axios[method](url, this.editedItem)
        .then((response) => {
          if (response.data.data && response.data.data.id) {
            let returnedObject = response.data.data;
            if (!this.editedItem.id) {
              // add the new item to items state
              this.items.push(returnedObject);
            } else {
              Object.assign(this.items[this.editedIndex], returnedObject);
            }
            this.editedItem = this.defaultItem;
          }
          this.close();
          this.setSuccess(successMessage);
        })
        .catch((error) => {
          debugger;
          if (
            error.response &&
            error.response.data &&
            error.response.data.errors
          ) {
            this.valid = false;
            let formErrors = error.response.data.errors;
            for (const [key, value] of Object.entries(formErrors)) {
              this.errors[key] = value;
            }
          }
          if (
            error.response &&
            error.response.data &&
            error.response.data.message
          ) {
            this.formErrorMessage = error.response.data.message;
          }
          this.setError(error);
        })
        .finally(() => {
          this.stopLoading();
        });
    },

    updateEditedModelRoi(value) {
      let _this = this;
      debugger;
      // rob: if is a new setup, clean all roi_id
      if (!_this.editedItem.hasOwnProperty("id")) {
        _this.editedItem.roi_desktop = null;
        _this.editedItem.roi_mobile = null;
        _this.editedItem.roi_tablet = null;
      }

      this.partners.some(function (item) {
        if (item.id == value.id) {
          item.rois.forEach(function (roi) {
            let availableAttribute = "roi_" + roi.device.toLowerCase();
            _this[availableAttribute] = roi.roi_id;
            // fix by rob
            // _this.editedItem[attribute] = roi.roi_id;
          });

          // rob: is true only for new setup
          if (
            (_this.editedItem.roi_desktop == null &&
              _this.editedItem.roi_mobile == null &&
              _this.editedItem.roi_tablet == null) ||
            (_this.editedItem.roi_desktop !== null &&
              _this.editedItem.roi_desktop !== _this.roi_desktop)
          ) {
            _this.editedItem.roi_desktop = _this.roi_desktop;
            _this.editedItem.roi_mobile = _this.roi_mobile;
            _this.editedItem.roi_tablet = _this.roi_tablet;
          }

          return true;
        }
      });
      this.errors["partner.name"] = [];
    },

    roiColor(device) {
      let attr = "roi_" + device.toLowerCase();
      if (this.editedItem[attr]) {
        return "success";
      }
      return "error";
    },

    validateRoiCheckboxes() {
      let sum = 0;
      let _this = this;
      this.deviceTypes.forEach(function (device) {
        let attribute = "roi_" + device.toLowerCase();
        if (_this.editedItem[attribute] && _this.editedItem[attribute] > 0) {
          sum += _this.editedItem[attribute];
        }
        _this.errors[attribute] = [];
      });
      if (sum <= 0) {
        this.valid = false;
        return false;
      }
      return true;
    },

    resetForm() {
      let _this = this;
      this.deviceTypes.forEach(function (device) {
        _this["roi_" + device.toLowerCase()] = null;
      });

      for (let prop in this.errors) {
        if (Object.prototype.hasOwnProperty.call(this.errors, prop)) {
          this.errors[prop] = [];
        }
      }

      this.formErrorMessage = "";
      this.valid = true;

      this.$refs.form.resetValidation();
    },

    saveRangeMenu(range) {
      this.errors.active_range = [];
      this.$refs.rangeMenu.save(range);
    },

    eventIcon(event) {
      switch (event) {
        case "created":
          return "mdi-plus";
        case "updated":
          return "mdi-pencil";
        case "deleted":
          return "mdi-trash";
      }
      return "mdi-set-none";
    },

    eventColor(event) {
      switch (event) {
        case "created":
          return "primary";
        case "updated":
          return "secondary";
        case "deleted":
          return "error";
      }
      return "mdi-set-none";
    },

    formatRelatedKeywordProvider(value) {
      let p = _.find(this.available_related_keywords_providers, function (o) {
        return o.alias == value;
      });
      if (p) {
        return p.formatted_name;
      }

      return "";
    },

    formatMode(value) {
      let p = _.find(this.modes, function (o) {
        return o.value == value;
      });
      if (p) {
        return p.text;
      }

      return "";
    },
  },

  computed: {
    ...mapGetters("config", [
      "modes",
      "partners",
      "revenue_partners",
      "available_sources",
      "available_related_keywords_providers",
      "available_share_processor_providers",
    ]),

    formTitle() {
      return this.editedIndex === -1 ? "New Setup" : "Edit Setup";
    },

    editedPartnerIdLabel() {
      if (this.editedItem.partner && this.editedItem.partner.id) {
        return "Partner id: " + this.editedItem.partner.id;
      }
      return "";
    },

    relatedNumberHint() {
      if (this.editedItem.mode != "scouting") {
        return "NOTE: This number does NOT include the primary keyword, which is always added.";
      }
      let relatedPercentage =
        (100 - this.editedItem.primary_percentage) /
        this.editedItem.related_number;
      relatedPercentage = Number(relatedPercentage.toFixed(2));
      return relatedPercentage + " % each one";
    },

    aggRevenueDataHoursHint() {
      return (
        "Use the aggregated revenue data in the last " +
        this.editedItem.parameters.agg_revenue_data_hours / 24 +
        " day(s)."
      );
    },

    roiCheckboxHint() {
      if (!this.validateRoiCheckboxes()) {
        return "Almost a ROI id must be checked";
      }
      return "";
    },

    showErrorMessage() {
      let ret = false;
      if (this.formErrorMessage.length > 0) {
        for (let prop in this.errors) {
          if (Object.prototype.hasOwnProperty.call(this.errors, prop)) {
            ret = ret || this.errors[prop].length > 0;
          }
        }
      }
      return ret;
    },
  },

  watch: {
    dialog(val) {
      val || this.close();
    },
    dialogDelete(val) {
      val || this.closeDelete();
    },
    dialogDetails(val) {
      if (!val) {
        this.closeDetails();
      } else {
        this.detailsLoading = true;
        let url = api.path("setup") + "/" + this.editedItem.id;
        axios
          .get(url, this.editedItem)
          .then((response) => {
            let details = response.data.data;
            console.log(details);
            if (details.audits) {
              this.details.audits = details.audits;
            }
          })
          .catch((error) => {
            this.setError(error);
          })
          .finally(() => {
            this.detailsLoading = false;
          });
      }
    },
  },

  created() {
    this.initialize();
  },

  filters: {
    formatDate(value) {
      if (value) {
        return moment(String(value)).format("YYYY-MM-DD");
      }
    },
  },
};
</script>

<style scope lang="scss">
//@import "@/_variables.scss";
// #related_number .v-messages__message {
//   color: $primary;
// }

//td {
/*vertical-align: top;*/
//}
//.shrinked_text {
//  /* font-size: 0.7vw; */
//}
.smallcaption {
  font-size: 0.65rem !important;
}

.v-data-table > .v-data-table__wrapper > table > tbody > tr > td,
.v-data-table > .v-data-table__wrapper > table > thead > tr > th,
.v-data-table > .v-data-table__wrapper > table > tfoot > tr > td {
  font-size: 0.7rem !important;
}
</style>
